import { Component, ViewChild, OnInit, Input, OnDestroy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { EudsDataTableComponent, EudsFilterComponent, EudsPaginationComponent, IMyDateModel } from '@eucoms/common-components/design-system';
import { DrawerComponent } from '../../components/drawer/drawer.component';
import { environment } from '../../../environments/environment';
import { ApiService, TouchPointFilterParams } from '@app/services/api.service';
import { TouchpointFormComponent } from '../touchpoint-form/touchpoint-form.component';
import { AppConfigService } from '@eucoms/common-components';
import {
  CustomerTouchPoint, OpCo as CustTpOpCo,
  InteractionChannelObject, JourneyDefinition} from '@eucoms/customer-touchpoints-tracker';
import { ProductWithDefinition } from '@app/model/products';
import { getChannelIcon } from '../../utils/icon-helpers';

@Component({
  selector: 'app-touchpoints',
  templateUrl: './touchpoints.component.html',
  styleUrls: ['../../app.component.scss', './touchpoints.component.scss'],
})
export class TouchPointsComponent implements OnInit, OnDestroy {
  // Whether this is a production build or not
  isProduction: boolean = environment.production;
  submittedTouchpointData: any;
  errorMessage = '';

  // The OpCo Selected in the base app component
  @Input() opCoSelected: string;

  opcoFieldError = false;

  // The data table
  @ViewChild('tableTouchpoints') tableTouchpoints: EudsDataTableComponent;

  // The child pagination component
  @ViewChild('paginationTouchpoints') paginationTouchpoints: EudsPaginationComponent;

  // The child drawer component
  @ViewChild('drawer') drawer: DrawerComponent;

  // The child filter components for channel, product, journey, and step
  @ViewChild('eudsFilterChannel') eudsFilterChannel: EudsFilterComponent;
  @ViewChild('eudsFilterProduct') eudsFilterProduct: EudsFilterComponent;
  @ViewChild('eudsFilterJourney') eudsFilterJourney: EudsFilterComponent;
  @ViewChild('eudsFilterStep') eudsFilterStep: EudsFilterComponent;

  // Add touchpoint form
  @ViewChild(TouchpointFormComponent) touchpointForm: TouchpointFormComponent;

  // The initial data set (should come from a service)
  allTouchpointsData: any[] = [];

  // The data set to be paged
  fetchedTouchpointsData: any[] = [];

  // Original data from server (before text search filtering)
  originalTouchpointsData: any[] = [];

  // The data to be shown on the page
  displayedTouchpointsData: Array<any> = [];

  // Data table column definitions
  columnDefsTouchpoints: any[] = [
    {
      'dataField': 'accountNumber',
      'header': 'Account Number',
      'sortable': true
    },
    {
      'dataField': 'customerInfo.customerNumber',
      'header': 'Customer Number',
      'sortable': true
    },
    {
      'dataField': 'customerInfo.phoneNumber',
      'header': 'Phone Number',
      'sortable': true
    },
    {
      'dataField': 'timestamp',
      'header': 'Date',
      'sortable': true
    },
    {
      'dataField': 'processingContext?.product',
      'header': 'Product',
      'sortable': true
    },
    {
      'dataField': 'processingContext?.journey',
      'header': 'Journey',
      'sortable': true
    },
    {
      'dataField': 'processingContext?.step',
      'header': 'Step',
      'sortable': true
    },
    {
      'dataField': 'processingContext?.resultDescription',
      'header': 'Result Description',
      'sortable': false
    },
    {
      'dataField': '',
      'header': 'View',
      'sortable': false
    }
  ];

  // The list of options for users to choose how many items per page to show
  itemsPerPageOptions: Array<number> = [25, 50, 100];
  // The chosen number of items per page to show
  itemsPerPage: number = 25;
  // The total number of items in the data set
  totalTouchpoints: number = 0;
  // The current page displayed
  currentPage: number = 1;

  // Filter field values
  filterAccountNumber: string = '';
  filterCustomerNumber: string = '';
  filterPhoneNumber: string = '';
  filterCreatedDate: string = '';
  filterProduct: string[] | null = null;
  filterJourney: string[] | null = null;
  filterStep: string[] | null = null;
  filterChannel: string[] | null = null;
  filterDescription: string = '';

  // Search text filter
  searchText: string = '';
  filterCount: number = 0;
  resultMessageText: string = '';

  // The date ranges to filter the touchpoints table by
  optionsDateFilter = [
    {
      value: '1hr',
      text: 'Last 1 hour'
    },
    {
      value: '6hr',
      text: 'Last 6 hours'
    },
    {
      value: '24hr',
      text: 'Last 24 hours'
    },
    {
      value: '1wk',
      text: 'Last week'
    },
    {
      value: '2wk',
      text: 'Last 2 weeks'
    },
    {
      value: '1mo',
      text: 'Last month'
    },
    {
      value: 'custom',
      text: 'Custom date range'
    }
  ];

  // Whether to show the date range date picker filter
  showDateRangeFilter: boolean = false;

  // The date picker filter value
  createdDateRange: IMyDateModel;

  // The start and end date filters to use for the API
  filterStartDate: string = '';
  filterEndDate: string = '';

  // Product definitions fetched from the API
  productDefinitions: ProductWithDefinition[] = [];

  // The selected product
  selectedProduct: string = '';

  // Alternate names for the selected product (to show in tooltip)
  productAlternativeNames: string[] = ['(None)'];

  // The journeys to show in the select, based on the selected product
  productJourneys: JourneyDefinition[] = [];

  // The selected journey
  selectedJourney: string = '';

  // Alternate names for the selected journey (to show in tooltip)
  journeyAlternativeNames: string[] = ['(None)'];

  // The steps to filter the touchpoints table by
  optionsStepFilter: any[] = [];

  // The channels to filter the touchpoints table by
  optionsChannelFilter = Object.values(InteractionChannelObject).map(str => ({ text: str }));

  // Use getChannelIcon function from icon helpers
  getChannelIcon = getChannelIcon;

  // The header text for the slide-in drawer panel
  drawerHeading: string = '';

  // Whether the add touchpoint panel is open or not
  isAddTouchpointPanelOpen: boolean = false;

  // The new touchpoint to add (json format?)
  newTouchpointJSON: any;

  // Whether the touchpoint creation was successful or not
  createTouchpointAttempted: boolean = false;
  isAddTouchpointSuccessful: boolean = false;
  isTouchpointDataValid: boolean = true;

  // Selected touchpoint to view details for
  selectedTouchpoint: any = null;

  // The touchpoint output to show in the drawer panel
  selectedTouchpointOutput: string = '';

  // Whether the view touchpoint panel is open or not
  isViewTouchpointDetailsPanelOpen: boolean = false;

  // Whether to show mock data instead
  hasMockData: boolean = false;
  isLoading: boolean = false;
  private routeSubscription: Subscription;
  private queryParamsSubscription: Subscription;
  private pendingTouchpointId: string | null = null;
  private isHandlingDeepLink: boolean = false;

  constructor(
    private appConfig: AppConfigService,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.opCoSelected ??= this.appConfig.config.opCo;

    // Check query params synchronously before subscriptions to prevent race conditions
    const currentQueryParams = this.route.snapshot.queryParams;
    const touchpointId = currentQueryParams['touchpointId'];
    const accountNumber = currentQueryParams['accountNumber'];

    if (touchpointId && accountNumber) {
      // Set flag immediately to prevent route params subscription from firing
      this.isHandlingDeepLink = true;
    }

    // Subscribe to query params for deep linking (check this first before route params)
    this.queryParamsSubscription = this.route.queryParams.subscribe(queryParams => {
      const touchpointId = queryParams['touchpointId'];
      const accountNumber = queryParams['accountNumber'];

      if (touchpointId && accountNumber) {

        // Set flag to prevent route params from also calling applyFilters
        this.isHandlingDeepLink = true;

        this.filterAccountNumber = accountNumber;
        this.pendingTouchpointId = touchpointId;

        // Use Promise to ensure Angular bindings are updated before applying filters
        Promise.resolve().then(() => {
          this.applyFilters();
        });
      }
    });

    // Subscribe to route params for deep linking
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      // Skip if we're handling a deep-link via query params to prevent duplicate applyFilters() calls
      if (this.isHandlingDeepLink) {
        return;
      }

      const accountNumberFromRoute = params.get('accountNumber');
      if (accountNumberFromRoute) {
        this.filterAccountNumber = accountNumberFromRoute;

        this.applyFilters();
      } else {
        this.applyFilters();
      }
    });

    // Fetch product definitions
    this.fetchProductDefinitions();
  }

  get currentTimeZone(): string {
    return this.opCoSelected.toLowerCase() === 'comed' ? 'America/Chicago' : 'America/New_York';
  }

  ngOnDestroy(): void {
    // Cleanup subscriptions
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }

    // Reset state to prevent pollution on subsequent visits
    this.isHandlingDeepLink = false;
    this.pendingTouchpointId = null;
  }

  fetchProductDefinitions(): void {
    // Fetch product definitions for product, journey, and step filters
    this.apiService.getProductsAndDefinitions()
      .pipe(
        map((response: ProductWithDefinition[]) => {
          if (response && response.length > 0) {
            return response;
          } else {
            console.warn('No products data received from API.');
            return [];
          }
        }),
        finalize(() => {
          console.log('Products data retrieval complete.');
        }),
        catchError((error) => {
          console.error('Error getting products data: ', error);
          return of([]);
        })
      )
      .subscribe((data: ProductWithDefinition[]) => {
        this.productDefinitions = data;
      });
  }

  openPanelToAddTouchpoint(): void {
    if (this.drawer.drawerOpen) {
      this.drawer.closeDrawer();
    }
    this.createTouchpointAttempted = false;
    this.isAddTouchpointSuccessful = false;
    this.drawerHeading = 'Add Touchpoint';
    this.isViewTouchpointDetailsPanelOpen = false;
    this.isAddTouchpointPanelOpen = true;
    this.drawer.openDrawer();
    this.errorMessage = '';
    this.opcoFieldError = false;

    if (this.touchpointForm) {
      // convert the string into the OpCo enum
      this.touchpointForm.formModel.opCo = this.opCoSelected as CustTpOpCo;
    }
  }

  addTouchpoint(): void {
    this.touchpointForm.onSubmit(this.touchpointForm.touchpointForm);
  }

  onFormSubmitted(result: { valid: boolean; data: any }) {
    if (!result.valid) {
      this.isTouchpointDataValid = false;
      this.createTouchpointAttempted = true;
      this.isAddTouchpointSuccessful = false;
      return;
    }

    this.submittedTouchpointData = result.data;
    this.isTouchpointDataValid = true;
    this.isLoading = true;
    this.opcoFieldError = false;

    this.apiService.createTouchpoint(result.data).subscribe({
      next: response => {
        if (response.status === 201 || response.status === 202) {
          this.createTouchpointAttempted = true;
          this.isAddTouchpointSuccessful = true;
          this.opcoFieldError = false;
        }
      },
      error: err => {
        this.isLoading = false;
        this.createTouchpointAttempted = true;
        this.isAddTouchpointSuccessful = false;
        if (err && err.status === 400) {
          this.errorMessage =
            'Touchpoint request failed: OpCo mismatch. Please ensure the OpCo value entered aligns with the OpCo you are currently in.';
          this.opcoFieldError = true;
        } else {
          this.errorMessage = 'Something went wrong. Please try again.';
        }
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }


  cancelTouchpointAddition(): void {
    this.drawer.closeDrawer();
  }

  closeTouchpointAdditionPanel(): void {
    this.drawer.closeDrawer();
  }

  clickViewTouchpointDetails($event: any, touchpoint: any): void {
    // View details (full json) for the selected touchpoint in the drawer panel
    if (this.drawer.drawerOpen) {
      this.drawer.closeDrawer();
    }
    this.selectedTouchpoint = touchpoint;
    this.isAddTouchpointPanelOpen = false;
    this.isViewTouchpointDetailsPanelOpen = true;
    this.drawerHeading = 'Touchpoint Details';
    this.selectedTouchpointOutput = JSON.stringify(touchpoint, null, '\t');
    this.drawer.openDrawer();
  }

  clickViewSummaryDetails($event: any, touchpoint: any): void {
    // View summary for the selected touchpoint in the drawer panel
    if (this.drawer.drawerOpen) {
      this.drawer.closeDrawer();
    }
    this.selectedTouchpoint = touchpoint;
    this.isAddTouchpointPanelOpen = false;
    this.isViewTouchpointDetailsPanelOpen = false;
    this.drawerHeading = 'Summary Details';
    this.drawer.openDrawer();
  }

  // Navigation within the displayed touchpoints list when viewing details
  private getCurrentTouchpointIndex(): number {
    if (!this.selectedTouchpoint || !this.displayedTouchpointsData?.length) return -1;
    return this.displayedTouchpointsData.findIndex(tp => tp?.id === this.selectedTouchpoint?.id);
  }

  canGoToPrevious(): boolean {
    const idx = this.getCurrentTouchpointIndex();
    return idx > 0;
  }

  canGoToNext(): boolean {
    const idx = this.getCurrentTouchpointIndex();
    return idx >= 0 && idx < (this.displayedTouchpointsData.length - 1);
  }

  private selectTouchpointByIndex(index: number): void {
    if (index < 0 || index >= this.displayedTouchpointsData.length) return;
    const nextTp = this.displayedTouchpointsData[index];
    if (!nextTp) return;
    // Reuse existing view method to ensure consistent drawer state updates
    this.clickViewTouchpointDetails(null, nextTp);
  }

  goToPreviousTouchpoint(): void {
    const idx = this.getCurrentTouchpointIndex();
    if (idx > 0) {
      this.selectTouchpointByIndex(idx - 1);
    }
  }

  goToNextTouchpoint(): void {
    const idx = this.getCurrentTouchpointIndex();
    if (idx >= 0 && idx < (this.displayedTouchpointsData.length - 1)) {
      this.selectTouchpointByIndex(idx + 1);
    }
  }

  clickViewGoToSummaryDetails($event: any, touchpoint: any): void {
    this.router.navigate([this.appConfig.config.opCo, 'summary', 'account-number', touchpoint.accountNumber]);
  }

  // Load a specific touchpoint by ID and open the drawer (for deep linking)
  loadTouchpointById(touchpointId: string): void {

    // First, check in all fetched data (not just current page because pagination and such)
    let touchpoint = this.fetchedTouchpointsData.find(
      tp => tp.id === touchpointId
    );

    if (touchpoint) {
      this.clickViewTouchpointDetails(null, touchpoint);
    } else {
      // Touchpoint not found in fetched data, try to load it directly from API
      this.apiService.getTouchpointById(touchpointId).subscribe(
        (tp) => {
          if (tp) {
            this.clickViewTouchpointDetails(null, tp);
          } else {
            console.error('Touchpoint not found in API with ID:', touchpointId);
          }
        },
        (error) => {
          console.error('Error fetching touchpoint by ID:', error);
        }
      );
    }
  }


  // Function to sort the data
  public sortTableData(event: any) {
    const { field: sortField, order: sortOrder } = event;
    if (sortOrder !== 'none') {
      this.fetchedTouchpointsData.sort((a, b) => {
        const aVal = this.getNestedValue(a, sortField);
        const bVal = this.getNestedValue(b, sortField);

        if (aVal == null) return sortOrder === 'ascending' ? -1 : 1;
        if (bVal == null) return sortOrder === 'ascending' ? 1 : -1;
        if (aVal > bVal) return sortOrder === 'ascending' ? 1 : -1;
        if (aVal < bVal) return sortOrder === 'ascending' ? -1 : 1;
        return 0;
      });
    }

    this.columnDefsTouchpoints.forEach(col => {
      col.sortOrder = col.dataField === sortField ? sortOrder : 'none';
    });
    this.showPageItemsForPagination(this.currentPage);
  }

  // Function to set the data shown based on the page chosen
  public onChangePage(page: number = 1): void {
    this.currentPage = page;
    if (this.paginationTouchpoints) {
      this.showPageItemsForPagination(page);
    }
  }

  filterDataSource(event: any): void {
    this.searchText = event;
    let tableData = [...this.originalTouchpointsData];

    if (event) {
      const search = this.searchText.toLowerCase();
      tableData = tableData.filter(touchpoint =>  {
        return (touchpoint.accountNumber as string)?.toLowerCase().includes(search)
          || (touchpoint.customerInfo?.customerNumber as string)?.toLowerCase().includes(search)
          || (touchpoint.customerInfo?.phoneNumber as string)?.toLowerCase().includes(search)
          || (touchpoint.processingContext?.product as string)?.toLowerCase().includes(search)
          || (touchpoint.processingContext?.journey as string)?.toLowerCase().includes(search)
          || (touchpoint.processingContext?.step as string)?.toLowerCase().includes(search)
          || (touchpoint.channel as string)?.toLowerCase().includes(search)
          || (touchpoint.processingContext?.resultDescription as string)?.toLowerCase().includes(search);
      });
    }

    this.filterCount = tableData.length;
    this.resultMessageText = `${this.filterCount} touchpoints found`;

    this.fetchedTouchpointsData = tableData;
    this.totalTouchpoints = tableData.length;

    this.currentPage = 1;
    if (this.paginationTouchpoints) {
      this.paginationTouchpoints.currentPage = 1;
    }

    this.showPageItemsForPagination(this.currentPage);
  }

  resetDataSource(event: any): void {
    this.searchText = '';

    this.fetchedTouchpointsData = [...this.originalTouchpointsData];
    this.totalTouchpoints = this.originalTouchpointsData.length;
    this.filterCount = this.totalTouchpoints;
    this.resultMessageText = `${this.filterCount} touchpoints found.`;

    this.currentPage = 1;
    if (this.paginationTouchpoints) {
      this.paginationTouchpoints.currentPage = 1;
    }

    this.showPageItemsForPagination(this.currentPage);
  }

  // Function to reset the data shown when the items to show per page has been changed
  public onChangeItemsPerPage(items: any): void {
    this.itemsPerPage = items;
    if (this.paginationTouchpoints) {
      this.showPageItemsForPagination(this.paginationTouchpoints.currentPage);
    }
  }

  // Function to show a subset of the data based on the current page
  private showPageItemsForPagination(page: number = 1): void {
    const startItem = (page - 1) * this.itemsPerPage;
    const endItem = page * this.itemsPerPage;
    this.totalTouchpoints = this.fetchedTouchpointsData.length ?? 0;
    this.displayedTouchpointsData = this.fetchedTouchpointsData.slice(
      startItem,
      endItem
    );
  }

  // Function to safely get nested property value for sorting/filtering
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((o, key) => (o != null ? o[key] : undefined), obj);
  }

  applyFilters(forceRefresh: boolean = false): void {
    this.isLoading = true;
    this.hasMockData = false;
    this.searchText = '';

    // Set date range values (filterStartDate and filterEndDate) based on date filter(s) selected
    if (this.filterCreatedDate !== 'custom') {
      this.filterCreatedDateValueChanged(this.filterCreatedDate);
    }
    else {
      this.createdDateRangeChanged(this.createdDateRange);
    }

    const filterParams: TouchPointFilterParams = {
      accountNumber: this.filterAccountNumber || null,
      customerNumber: this.filterCustomerNumber || null,
      phoneNumber: this.filterPhoneNumber || null,
      product: this.filterProduct || null,
      journey: this.filterJourney || null,
      step: this.filterStep || null,
      channel: this.filterChannel || null,
      startTimeStamp: this.filterStartDate || null,
      endTimeStamp: this.filterEndDate || null
    };

  this.apiService.getTouchpointsByFilter(filterParams, forceRefresh)
      .pipe(
        map((response: CustomerTouchPoint[]) => {
          if (response && response.length > 0) {
            this.hasMockData = false;
            return response;
          } else {
            console.warn('No data received from API filter, showing empty list.');
            this.hasMockData = false;
            return [];
          }
        }),
        finalize(() => {
          this.isLoading = false;
          console.log('Filter touchpoints data complete.');
        }),
        catchError((error) => {
          console.error('Error filtering touchpoints data: ', error);
          this.hasMockData = false;
          this.isLoading = false;
          // Reset deep-link flags on error to prevent permanent blocking
          this.isHandlingDeepLink = false;
          this.pendingTouchpointId = null;
          return of([]);
        })
      )
      .subscribe((data: CustomerTouchPoint[]) => {
        // client‑side filtering on Description (all other filters should be server-side)
        const filtered = data.filter(tp => {
          if (this.filterDescription &&
            !tp.processingContext?.resultDescription?.toLowerCase().includes(this.filterDescription?.toLowerCase())
          ) return false;
          return true;
        });

        this.fetchedTouchpointsData = filtered;
        this.originalTouchpointsData = [...filtered];
        this.totalTouchpoints = filtered.length;
        this.filterCount = this.totalTouchpoints;
        this.resultMessageText = `${this.filterCount} touchpoints found.`;

        // reset any previous sort state whenever filters are applied
        this.columnDefsTouchpoints.forEach((col: any) => {
          col.sortOrder = 'none';
        });

        this.showPageItemsForPagination(this.currentPage);

        // Handle deep-linking load specific touchpoint if pending
        this.handlePendingTouchpointLoad();
      });

  }

  /**
   * Handles loading a pending touchpoint after filters are applied (for deep-linking)
   */
  private handlePendingTouchpointLoad(): void {
    if (this.pendingTouchpointId) {
      const touchpointIdToLoad = this.pendingTouchpointId;
      this.pendingTouchpointId = null; // Clear it so it only runs once

      if (!environment.production) {
        console.log('Loading pending touchpoint:', touchpointIdToLoad);
        console.log('Fetched touchpoints count:', this.fetchedTouchpointsData.length);
      }

      this.loadTouchpointById(touchpointIdToLoad);

      // Clear the flag after handling
      this.isHandlingDeepLink = false;
    }
  }

  clearFilters(): void {
    // Reset filter and model properties
    this.filterAccountNumber = '';
    this.filterCustomerNumber = '';
    this.filterPhoneNumber = '';
    this.filterCreatedDate = '';
    this.createdDateRange = null;
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.selectedProduct = null;
    this.filterProduct = null;
    this.productAlternativeNames = ['(None)'];
    this.selectedJourney = null;
    this.filterJourney = null;
    this.journeyAlternativeNames = ['(None)'];
    this.eudsFilterStep.clearAllSelectedOptions();
    this.filterStep = null;
    this.eudsFilterChannel.clearAllSelectedOptions();
    this.filterChannel = null;
    this.filterDescription = '';

    // Re-apply filters which will now fetch with empty criteria
    this.applyFilters();
  }

  onOpenDrawer(event: any): void {
    // Drawer opened
  }

  onCloseDrawer(event: any): void {
    // TODO: Reset focus back to original button that opened the drawer ?
    if (this.createTouchpointAttempted
      && this.isTouchpointDataValid
      && this.isAddTouchpointSuccessful) {
      console.log('Drawer closed after successful creation of touchpoint. Refreshing data...');
      this.applyFilters(); // Refresh the data using the current filters
    }
    this.isAddTouchpointPanelOpen = false;
    this.isViewTouchpointDetailsPanelOpen = false;
    this.createTouchpointAttempted = false;
    this.isAddTouchpointSuccessful = false;
    this.selectedTouchpoint = null;
  }

  filterCreatedDateValueChanged(event: any) {
    if (event === 'custom') {
      this.showDateRangeFilter = true;
      this.createdDateRange = null;
      // Will update date filters in dateChanged event for date picker instead
      this.setDateRangeValuesForFilter('', '');
    }
    else {
      this.showDateRangeFilter = false;
      let startTimeStamp, endTimeStamp: Date | null;
      // Get a date object for the current date and time
      let d = new Date();
      switch (event) {
        case '1hr':
          startTimeStamp = new Date(d.setHours(d.getHours() - 1));
          break;
        case '6hr':
          startTimeStamp = new Date(d.setHours(d.getHours() - 6));
          break;
        case '24hr':
          startTimeStamp = new Date(d.setHours(d.getHours() - 24));
          break;
        case '1wk':
          startTimeStamp = new Date(d.setDate(d.getDate() - 7));
          break;
        case '2wk':
          startTimeStamp = new Date(d.setDate(d.getDate() - 14));
          break;
        case '1mo':
          let m = d.getMonth();
          d.setMonth(d.getMonth() - 1);
          // If still in same month, set date to last day of previous month
          if (d.getMonth() == m) d.setDate(0);
          startTimeStamp = new Date(d);
          break;
        default:
          // Default to none
          startTimeStamp = null;
          break;
      }
      if (startTimeStamp) {
        this.setDateRangeValuesForFilter(startTimeStamp.toISOString(), '');
      }
    }
  }

  createdDateRangeChanged(event: any) {
    if (this.showDateRangeFilter) {
      if (event?.dateRange?.beginJsDate && event?.dateRange?.endJsDate) {
        let startTimeStamp, endTimeStamp: Date;
        startTimeStamp = event.dateRange.beginJsDate;
        endTimeStamp = new Date(event.dateRange.endJsDate.setHours(23, 59, 59, 999));
        this.setDateRangeValuesForFilter(startTimeStamp.toISOString(), endTimeStamp.toISOString());
      }
      else {
        this.setDateRangeValuesForFilter('', '');
      }
    }
  }

  setDateRangeValuesForFilter(startDate: string, endDate: string) {
    this.filterStartDate = startDate;
    this.filterEndDate = endDate;
  }

  filterChannelSelectedOptionsChanged(selectedOptions: any) {
    if (selectedOptions?.length > 0) {
      // Set filter values for channel
      this.filterChannel = selectedOptions;
    }
    else {
      // For no options, set null filter value for channel
      this.filterChannel = null;
    }
  }

  filterProductSelectedOptionChanged(selectedProductId: string) {
    // Clear filters and model properties
    this.filterProduct = null;
    this.selectedJourney = null;
    this.filterJourney = null;
    this.eudsFilterStep.clearAllSelectedOptions();
    this.filterStep = null;
    this.productAlternativeNames = ['(None)'];
    // Find the product definition by id
    const product = this.productDefinitions.find(p => p.id === selectedProductId);
    // Set the product filter to include alternative names
    if (product) {
      const altNames = product.definition?.alternativeNames ?? [];
      this.filterProduct = [product.name, ...altNames];
      this.productAlternativeNames = altNames;
    }
    // Set the options for the journey select field, or an empty array if not found
    this.productJourneys = product && product.definition && product.definition.journeys
      ? product.definition.journeys
      : [];
  }

  filterJourneySelectedOptionChanged(selectedJourneyName: string) {
    // Clear filters and model properties
    this.filterJourney = null;
    this.eudsFilterStep.clearAllSelectedOptions();
    this.filterStep = null;
    this.journeyAlternativeNames = ['(None)'];
    // Find the journey by value
    const journey = this.productJourneys.find(j => j.name === this.selectedJourney);
    // Set the journey filter to include alternative names
    if (journey) {
      const altNames = journey.alternativeNames ?? [];
      this.filterJourney = [journey.name, ...altNames];
      this.journeyAlternativeNames = altNames;
    }
    // Set the options for the step filter, or an empty array if not found
    if (journey && journey.steps) {
      this.optionsStepFilter = Object.values(journey.steps).map(s => ({ text: s.name }));
    }
    else {
      this.optionsStepFilter = [];
    }
  }

  filterStepSelectedOptionsChanged(selectedOptions: any) {
    if (selectedOptions?.length > 0) {
      // Set filter values for step
      this.filterStep = selectedOptions;
    }
    else {
      // For no options, set null filter value for step
      this.filterStep = null;
    }
  }

  isJourneyFilterDisabled(): boolean {
    // Disable the journey filter if no product is selected
    if (this.selectedProduct?.length > 0) {
      return false;
    }
    return true;
  }

  isStepFilterDisabled(): boolean {
    // Disable the step filter if no journey is selected
    if (this.selectedJourney?.length > 0) {
      return false;
    }
    return true;
  }

  navigateToTesting(): void {
    this.router.navigate([this.opCoSelected, 'touchpoints', 'testing']);
  }

}
